import { z } from 'zod';
import { AppError, type ValidationIssue } from '../errors';
import type { MoneyTotal } from '../money';
import {
  addDaysLocal,
  diffDaysLocal,
  isLocalDate,
  type LocalDate,
  type LocalTime,
  localToUtc,
  type ResolvedTimeRange,
  resolveTimeRange,
  startOfLocalDayUtc,
  toLocalDate,
  toLocalTime,
} from '../time';
import { currencySchema, idSchema, localDateSchema, localTimeSchema, rateSchema } from './common';

/**
 * Czas (docs/3VTOS_MVP.md §7, DECISIONS: wpis ręczny od–do bez stopera, timesheet w stylu Clockify):
 * `time_entries` (packages/db/src/schema/time.ts). Moduł uprawnień: `time`.
 *
 * Zasady (pilnuje też baza, CHECK-i w schemacie):
 *  - czas w sekundach; `started_at`/`ended_at` w UTC, data i godziny użytkownika w Europe/Warsaw,
 *  - `duration_seconds = ended_at − started_at` (> 0); sprzeczne od / do / czas trwania = VALIDATION_ERROR,
 *  - wpis przez północ wymaga jawnej daty końca (`endDate`),
 *  - stawka (`rate`) to snapshot stawki klienta z chwili zapisu; kwota = `computeEntryAmount` z `@fos/shared/money`
 *    (stawka × sekundy / 3600, HALF_UP do grosza; nierozliczany albo bez stawki = 0,00),
 *  - wpis z `billing_draft_id` jest w zestawieniu (najwyżej jednym); `locked_at` = zamknięty w rozliczeniu (bez edycji).
 * Wejścia i wyjścia usług dopisuje agent modułu time.
 */

/**
 * Rozliczenie partnera (DECISIONS 2026-10-08 [P] „Rozliczenie między partnerami — godziny wpisywane raz”):
 * prowadzący zamyka rozliczenie partnera za okres (`closed`); „Otwórz ponownie” (`reopened`) zwalnia wpisy do zmian
 * i do nowego rozliczenia, rekord zostaje w historii.
 */
export const PARTNER_SETTLEMENT_STATUSES = ['closed', 'reopened'] as const;
export type PartnerSettlementStatus = (typeof PARTNER_SETTLEMENT_STATUSES)[number];
/** Sposób rozliczenia: gotówka, przelew, faktura (faktura nie jest konieczna). */
export const PARTNER_SETTLEMENT_METHODS = ['cash', 'transfer', 'invoice'] as const;
export type PartnerSettlementMethod = (typeof PARTNER_SETTLEMENT_METHODS)[number];
export const PARTNER_SETTLEMENT_METHOD_LABELS: Record<PartnerSettlementMethod, string> = {
  cash: 'gotówka',
  transfer: 'przelew',
  invoice: 'faktura',
};
/** Kwoty partnera są zawsze w walucie bazowej workspace'u (stawka partnera nie ma własnej waluty). */
export const PARTNER_CURRENCY = 'PLN';

export const TIME_ENTRY_SOURCES = ['manual', 'voice', 'ai', 'mcp'] as const;
export type TimeEntrySource = (typeof TIME_ENTRY_SOURCES)[number];
export const timeEntrySourceSchema = z.enum(TIME_ENTRY_SOURCES);

/** Najdłuższy pojedynczy wpis (walidacja wejścia; baza tego nie ogranicza). */
export const MAX_TIME_ENTRY_SECONDS = 24 * 3600;

/** Pola pieniężne wpisu: bez „Widzi stawki i kwoty” DTO, CSV i PDF ich nie zawierają (pola pominięte, nie null). */
export const TIME_ENTRY_MONEY_FIELDS = ['rate', 'amount'] as const;

export const timeEntryDescriptionSchema = z.string().trim().max(500, { message: 'Najwyżej 500 znaków' });

/** Powiązania wpisu: klient, projekt, usługa, zadanie (opcjonalne, ale spójne — ten sam klient). */
export const timeEntryLinksSchema = z.object({
  clientId: idSchema.nullable().optional(),
  projectId: idSchema.nullable().optional(),
  serviceId: idSchema.nullable().optional(),
  taskId: idSchema.nullable().optional(),
});
export type TimeEntryLinks = z.infer<typeof timeEntryLinksSchema>;

/**
 * Czas wpisu w Europe/Warsaw: data + początek + (koniec | czas trwania). Podane oba = muszą się zgadzać.
 * `endDate` tylko dla wpisu przez północ (jawnie).
 */
export const timeEntryTimingShape = {
  date: localDateSchema,
  start: localTimeSchema,
  end: localTimeSchema.nullable().optional(),
  endDate: localDateSchema.nullable().optional(),
  durationSeconds: z
    .number()
    .int()
    .positive({ message: 'Czas musi być dodatni' })
    .max(MAX_TIME_ENTRY_SECONDS, { message: 'Wpis nie może być dłuższy niż 24 h' })
    .nullable()
    .optional(),
};
export interface TimeEntryTiming {
  date: LocalDate;
  start: LocalTime;
  end?: LocalTime | null;
  endDate?: LocalDate | null;
  durationSeconds?: number | null;
}

/** Wynik `resolveEntryTiming`: chwile w UTC + lokalny koniec (do podglądu w formularzu). */
export interface ResolvedEntryTiming extends ResolvedTimeRange {
  /** koniec lokalnie (Europe/Warsaw) */
  end: LocalTime;
  endDate: LocalDate;
}

function timingError(message: string, issues: ValidationIssue[], extra: Record<string, unknown> = {}): AppError {
  return new AppError('VALIDATION_ERROR', message, { details: { issues, ...extra } });
}

/** Wpis przez północ bez jawnej daty końca (MVP §7). */
function crossesMidnightError(): AppError {
  return timingError(TIME_MESSAGES.crossesMidnight, [{ path: 'endDate', message: TIME_MESSAGES.endDateRequired }], {
    crossesMidnight: true,
  });
}

/**
 * Czas wpisu → `{ startedAt, endedAt, durationSeconds }` (MVP §7):
 *  - początek + koniec → czas trwania (DST: rzeczywiste sekundy),
 *  - początek + czas trwania → koniec,
 *  - wszystkie trzy → muszą się zgadzać (inaczej VALIDATION_ERROR „Koniec i czas trwania się nie zgadzają”),
 *  - przez północ bez `endDate` → VALIDATION_ERROR (pole `endDate`), także przy czasie trwania,
 *  - brak końca i czasu trwania, koniec ≤ początek, więcej niż 24 h → VALIDATION_ERROR.
 * Tej samej funkcji używają formularz (podgląd), parser zdania i usługi (zapis).
 */
export function resolveEntryTiming(input: TimeEntryTiming): ResolvedEntryTiming {
  const duration = input.durationSeconds ?? null;
  const endDateInput = input.endDate ?? null;
  let range: ResolvedTimeRange;

  if (input.end) {
    // „8:00–8:00” bez daty końca to zerowy czas, nie doba (resolveTimeRange uznałby to za przejście przez północ)
    if (input.end === input.start && (endDateInput === null || endDateInput === input.date)) {
      throw timingError(TIME_MESSAGES.endBeforeStart, [{ path: 'end', message: TIME_MESSAGES.endBeforeStart }]);
    }
    try {
      range = resolveTimeRange({ date: input.date, start: input.start, end: input.end, endDate: endDateInput });
    } catch (err) {
      const details = (err as AppError).details as { crossesMidnight?: boolean } | undefined;
      if (details?.crossesMidnight) throw crossesMidnightError();
      throw err;
    }
    if (duration !== null && duration !== range.durationSeconds) {
      throw timingError(
        'Koniec i czas trwania się nie zgadzają. Popraw jedno z nich.',
        [{ path: 'durationSeconds', message: 'Nie zgadza się z godzinami od–do' }],
        { conflicting: true },
      );
    }
  } else if (duration !== null) {
    const startedAt = localToUtc(input.date, input.start);
    const endedAt = new Date(startedAt.getTime() + duration * 1000);
    const computedEndDate = toLocalDate(endedAt);
    if (computedEndDate !== input.date && endDateInput !== computedEndDate) throw crossesMidnightError();
    if (endDateInput !== null && endDateInput !== computedEndDate) {
      throw timingError('Data końca nie zgadza się z czasem trwania.', [
        { path: 'endDate', message: 'Nie zgadza się z czasem trwania' },
      ]);
    }
    range = { startedAt, endedAt, durationSeconds: duration, crossesMidnight: computedEndDate !== input.date };
  } else {
    throw timingError('Podaj godzinę końca albo czas trwania.', [
      { path: 'end', message: 'Podaj koniec albo czas trwania' },
    ]);
  }

  if (range.durationSeconds > MAX_TIME_ENTRY_SECONDS) {
    throw timingError('Wpis nie może być dłuższy niż 24 h.', [{ path: 'durationSeconds', message: 'Najwyżej 24 h' }]);
  }
  return { ...range, end: toLocalTime(range.endedAt), endDate: toLocalDate(range.endedAt) };
}

/** Refinement Zod dla schematów z `timeEntryTimingShape` (te same reguły co `resolveEntryTiming`). */
export function timeEntryTimingRefinement(value: TimeEntryTiming, ctx: z.RefinementCtx): void {
  try {
    resolveEntryTiming(value);
  } catch (err) {
    const details = (err as AppError).details as { issues?: ValidationIssue[] } | undefined;
    const issues = details?.issues ?? [{ path: 'end', message: (err as Error).message }];
    for (const issue of issues) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: issue.path ? [issue.path] : [], message: issue.message });
    }
  }
}

export const timeEntryTimingSchema = z.object(timeEntryTimingShape).superRefine(timeEntryTimingRefinement);

/** Pola wpisu edytowalne przez osobę (osoba, kwota, źródło, blokada i zestawienie ustawia usługa). */
export const timeEntryFieldsShape = {
  ...timeEntryLinksSchema.shape,
  ...timeEntryTimingShape,
  description: timeEntryDescriptionSchema.default(''),
  /** domyślnie z projektu (`billable_default`); bez klienta zawsze false */
  billable: z.boolean().optional(),
  /** stawka indywidualna; brak = snapshot domyślnej stawki klienta (wymaga „Widzi stawki i kwoty”) */
  rate: rateSchema.nullable().optional(),
  currency: currencySchema.optional(),
};
export const timeEntryFieldsSchema = z.object(timeEntryFieldsShape).superRefine(timeEntryTimingRefinement);
export type TimeEntryFields = z.infer<typeof timeEntryFieldsSchema>;

/** Stan wpisu w rozliczeniach (wyliczany z `billable`, `billing_draft_id`, `locked_at`). */
export const TIME_ENTRY_BILLING_STATES = ['unbilled', 'in_draft', 'locked', 'non_billable'] as const;
export type TimeEntryBillingState = (typeof TIME_ENTRY_BILLING_STATES)[number];

export function timeEntryBillingState(e: {
  billable: boolean;
  billingDraftId: string | null;
  lockedAt: Date | string | null;
}): TimeEntryBillingState {
  if (!e.billable) return 'non_billable';
  if (e.lockedAt) return 'locked';
  if (e.billingDraftId) return 'in_draft';
  return 'unbilled';
}

/** Dwa wpisy tej samej osoby nachodzą na siebie (ostrzeżenie w UI, nie błąd). Przedziały [start, end). */
export function entriesOverlap(a: { startedAt: Date; endedAt: Date }, b: { startedAt: Date; endedAt: Date }): boolean {
  return a.startedAt.getTime() < b.endedAt.getTime() && b.startedAt.getTime() < a.endedAt.getTime();
}

// ── Raport (Figma 06 Czas / Raport) ─────────────────────────────────────────

/** Okresy raportu (DECISIONS: ten tydzień, ten miesiąc, poprzedni miesiąc, własny zakres). */
export const TIME_REPORT_PERIODS = ['this_week', 'this_month', 'last_month', 'custom'] as const;
export type TimeReportPeriod = (typeof TIME_REPORT_PERIODS)[number];
export const timeReportPeriodSchema = z.enum(TIME_REPORT_PERIODS);

/** Grupowanie raportu („Według projektu”, klienta, osoby, dnia). */
export const TIME_REPORT_GROUPS = ['project', 'client', 'user', 'day'] as const;
export type TimeReportGroup = (typeof TIME_REPORT_GROUPS)[number];
export const timeReportGroupSchema = z.enum(TIME_REPORT_GROUPS);

/** Zakres dat raportu (włącznie), daty lokalne. */
export interface LocalDateRange {
  from: LocalDate;
  to: LocalDate;
}

function monthStart(date: LocalDate): LocalDate {
  return `${date.slice(0, 7)}-01`;
}

function monthEnd(date: LocalDate): LocalDate {
  const [y, m] = date.split('-').map(Number) as [number, number];
  const next = m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`;
  return addDaysLocal(next, -1);
}

/** Poniedziałek tygodnia daty (tydzień w Polsce zaczyna się w poniedziałek). */
export function weekStart(date: LocalDate): LocalDate {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = niedziela
  return addDaysLocal(date, weekday === 0 ? -6 : 1 - weekday);
}

/**
 * Okres raportu → zakres dat lokalnych (włącznie). `custom` wymaga `range` (od ≤ do).
 * Przykład (dziś nd 4.10.2026): this_week = 28.09–4.10, this_month = 1–31.10, last_month = 1–30.09.
 */
export function reportPeriodRange(
  period: TimeReportPeriod,
  today: LocalDate,
  range?: Partial<LocalDateRange>,
): LocalDateRange {
  switch (period) {
    case 'this_week': {
      const from = weekStart(today);
      return { from, to: addDaysLocal(from, 6) };
    }
    case 'this_month':
      return { from: monthStart(today), to: monthEnd(today) };
    case 'last_month': {
      const prev = addDaysLocal(monthStart(today), -1);
      return { from: monthStart(prev), to: monthEnd(prev) };
    }
    case 'custom': {
      if (!range?.from || !range.to || !isLocalDate(range.from) || !isLocalDate(range.to)) {
        throw new AppError('VALIDATION_ERROR', 'Podaj zakres dat.', {
          details: { issues: [{ path: 'from', message: 'Podaj datę początku i końca' }] },
        });
      }
      if (range.to < range.from) {
        throw new AppError('VALIDATION_ERROR', 'Koniec zakresu jest przed początkiem.', {
          details: { issues: [{ path: 'to', message: 'Koniec przed początkiem' }] },
        });
      }
      return { from: range.from, to: range.to };
    }
  }
}

/**
 * Zakres dat lokalnych → półotwarty przedział UTC [start, end) do filtrowania `started_at`
 * (wpis należy do dnia, w którym się zaczął — jak w Clockify).
 */
export function localRangeToUtc(range: LocalDateRange): { start: Date; end: Date } {
  return { start: startOfLocalDayUtc(range.from), end: startOfLocalDayUtc(addDaysLocal(range.to, 1)) };
}

/** Filtry raportu i timesheetu (klocki wejść usług modułu time). `null` w powiązaniach = „bez” (np. bez klienta). */
export const timeReportFilterShape = {
  period: timeReportPeriodSchema.default('this_week'),
  from: localDateSchema.optional(),
  to: localDateSchema.optional(),
  clientId: idSchema.nullable().optional(),
  projectId: idSchema.nullable().optional(),
  userId: idSchema.optional(),
  billable: z.boolean().optional(),
};

/** Najdłuższy zakres raportu i listy wpisów (dni). */
export const MAX_TIME_RANGE_DAYS = 366;

export function timeReportFilterRefinement(
  v: { period?: TimeReportPeriod; from?: string; to?: string },
  ctx: z.RefinementCtx,
): void {
  if (v.period !== 'custom') return;
  if (!v.from) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['from'], message: 'Podaj datę początku' });
  if (!v.to) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['to'], message: 'Podaj datę końca' });
  if (v.from && v.to && v.to < v.from) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['to'], message: 'Koniec przed początkiem' });
  }
  if (v.from && v.to && v.to >= v.from && diffDaysLocal(v.from, v.to) >= MAX_TIME_RANGE_DAYS) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['to'], message: 'Najwyżej rok naraz' });
  }
}

export const timeReportFilterSchema = z.object(timeReportFilterShape).superRefine(timeReportFilterRefinement);
export type TimeReportFilter = z.input<typeof timeReportFilterSchema>;

/** Komunikaty modułu Czas (wspólne dla UI, AI i MCP). */
export const TIME_MESSAGES = {
  locked: 'Wpis jest zamknięty w wystawionym rozliczeniu. Korekta wymaga osobnego procesu.',
  /** wpis w zestawieniu, które czeka na akceptację albo jest wystawione */
  closed: 'Wpis jest zamknięty w rozliczeniu',
  inDraft: 'Wpis jest w zestawieniu. Najpierw usuń go z zestawienia.',
  archiveBilled: 'Wpis jest w rozliczeniu. Najpierw usuń go z zestawienia.',
  archived: 'Wpis jest usunięty. Przywróć go, żeby go zmienić.',
  billableWithoutClient: 'Wpis rozliczany wymaga klienta',
  projectOtherClient: 'Projekt należy do innego klienta',
  internalProjectWithClient: 'Projekt wewnętrzny nie ma klienta. Usuń klienta albo wybierz inny projekt.',
  serviceOtherClient: 'Usługa należy do innego klienta',
  taskOtherClient: 'Zadanie należy do innego klienta',
  taskOtherProject: 'Zadanie należy do innego projektu',
  archivedClient: 'Klient jest w archiwum',
  archivedProject: 'Projekt jest w archiwum',
  archivedService: 'Usługa jest w archiwum',
  overlap: 'Ten wpis nachodzi na inny Twój wpis',
  overlapOther: 'Ten wpis nachodzi na inny wpis tej osoby',
  noRate: 'Klient nie ma stawki — kwota wpisu wynosi 0 zł',
  crossesMidnight: 'Wpis przez północ: wskaż dzień zakończenia',
  endDateRequired: 'Wskaż dzień zakończenia',
  endBeforeStart: 'Koniec musi być po początku',
  otherUserForbidden: 'Wpis innej osoby może dodać albo zmienić tylko Manager albo Admin',
  userNotMember: 'Ta osoba nie jest aktywnym członkiem zespołu',
  rateForbidden: 'Stawkę może zmienić tylko osoba, która widzi stawki i kwoty',
  /** osoba z dostępem tylko do projektu klienta (bez całego klienta) */
  rateClientForbidden: 'Stawkę klienta ustawia osoba z dostępem do całego klienta',
  billableClientForbidden:
    '„Do rozliczenia” ustawia osoba z dostępem do całego klienta — wpis bierze ustawienie projektu',
  tasksModule: 'Brak dostępu do modułu Zadania',
  partnerSettled: 'Wpis jest w zamkniętym rozliczeniu partnera. Zmienisz go po ponownym otwarciu rozliczenia.',
  partnerRateForbidden: 'Stawkę partnera ustawia właściciel, admin albo manager',
  partnerOwnRateForbidden: 'Swoją stawkę partnera zmienia właściciel albo admin',
  settlementForbidden: 'Rozliczenie partnera zamyka i otwiera właściciel albo admin',
  settlementEmpty: 'W tym okresie nie ma nierozliczonych wpisów ze stawką partnera',
  settlementStale: 'Wpisy zmieniły się od podglądu. Odśwież podgląd i zamknij rozliczenie jeszcze raz.',
  settlementNotClosed: 'To rozliczenie jest już otwarte ponownie',
} as const;

// ── Wejścia usług (router `time`) ───────────────────────────────────────────

/** „Moje” = wpisy zalogowanej osoby, „Zespół” = wszystkie wpisy w zakresie danych osoby (przełącznik w menu). */
export const TIME_ENTRY_SCOPES = ['mine', 'team'] as const;
export type TimeEntryScope = (typeof TIME_ENTRY_SCOPES)[number];
export const timeEntryScopeSchema = z.enum(TIME_ENTRY_SCOPES);

/** Źródła, które może podać UI (AI i MCP wynikają z kanału aktora, nie z wejścia). */
export const TIME_ENTRY_INPUT_SOURCES = ['manual', 'voice'] as const;

const idempotencyKeySchema = z.string().trim().min(8).max(200);

/**
 * Nowy wpis (MVP §7): data + od + do (+ `endDate` przy przejściu przez północ) albo data + od + czas trwania.
 * Czas sprawdza usługa (`resolveEntryTiming`: komunikat i pole błędu), nie schemat — jeden komunikat dla UI, AI i MCP.
 * `rate` (także `null`) i `currency` wymagają „Widzi stawki i kwoty”; bez nich stawka = snapshot stawki klienta.
 * `userId` (wpis za inną osobę) — tylko Manager i Admin.
 */
export const createTimeEntryInput = z.object({
  ...timeEntryLinksSchema.shape,
  ...timeEntryTimingShape,
  description: timeEntryDescriptionSchema.default(''),
  billable: z.boolean().optional(),
  rate: rateSchema.nullable().optional(),
  currency: currencySchema.optional(),
  userId: idSchema.optional(),
  source: z.enum(TIME_ENTRY_INPUT_SOURCES).optional(),
  /** MCP, głos, AI: ponowienie z tym samym kluczem zwraca zapisany wpis (unikalny w workspace) */
  idempotencyKey: idempotencyKeySchema.optional(),
});
export type CreateTimeEntryInput = z.input<typeof createTimeEntryInput>;

/**
 * Edycja wpisu (pola pominięte = bez zmian). Zmiana samego początku zostawia godzinę końca (jak w Clockify);
 * `durationSeconds` bez `end` = nowy koniec. Zmiana klienta bez `rate` = snapshot stawki nowego klienta.
 */
export const updateTimeEntryInput = z.object({
  id: idSchema,
  version: z.number().int().positive(),
  clientId: idSchema.nullable().optional(),
  projectId: idSchema.nullable().optional(),
  serviceId: idSchema.nullable().optional(),
  taskId: idSchema.nullable().optional(),
  date: localDateSchema.optional(),
  start: localTimeSchema.optional(),
  end: localTimeSchema.nullable().optional(),
  endDate: localDateSchema.nullable().optional(),
  durationSeconds: timeEntryTimingShape.durationSeconds,
  description: timeEntryDescriptionSchema.optional(),
  billable: z.boolean().optional(),
  rate: rateSchema.nullable().optional(),
  currency: currencySchema.optional(),
  userId: idSchema.optional(),
});
export type UpdateTimeEntryInput = z.input<typeof updateTimeEntryInput>;

export const timeEntryVersionInput = z.object({ id: idSchema, version: z.number().int().positive() });
export const timeEntryIdInput = z.object({ id: idSchema });

/** „Niezafakturowane” (`unbilled`) = rozliczane i jeszcze w żadnym zestawieniu (jak Rozliczenia i Dziś). */
export const listTimeEntriesInput = z
  .object({
    scope: timeEntryScopeSchema.default('mine'),
    /** konkretna osoba (tylko przy `scope: 'team'`) */
    userId: idSchema.optional(),
    /** zakres dat (włącznie); domyślnie od poniedziałku 3 tygodnie wstecz do dziś */
    from: localDateSchema.optional(),
    to: localDateSchema.optional(),
    clientId: idSchema.nullable().optional(),
    projectId: idSchema.nullable().optional(),
    serviceId: idSchema.optional(),
    taskId: idSchema.optional(),
    billable: z.boolean().optional(),
    unbilled: z.boolean().optional(),
    billingStates: z.array(z.enum(TIME_ENTRY_BILLING_STATES)).min(1).optional(),
    /** tylko wpisy w archiwum („usunięte”) — do przywrócenia (MCP `list_time_entries`, przegląd Fali 12) */
    archived: z.boolean().optional(),
    limit: z.number().int().min(1).max(2000).default(500),
  })
  .superRefine((v, ctx) => {
    if (v.from && v.to && v.to < v.from) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['to'], message: 'Koniec przed początkiem' });
    }
    if (v.from && v.to && v.to >= v.from && diffDaysLocal(v.from, v.to) >= MAX_TIME_RANGE_DAYS) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['to'], message: 'Najwyżej rok naraz' });
    }
  });
export type ListTimeEntriesInput = z.input<typeof listTimeEntriesInput>;

/** Raport (Figma 06 Raport) i eksport CSV / PDF: okres, filtry, „Moje / Zespół”. */
export const timeReportInput = z
  .object({ ...timeReportFilterShape, scope: timeEntryScopeSchema.default('mine'), serviceId: idSchema.optional() })
  .superRefine(timeReportFilterRefinement);
export type TimeReportInput = z.input<typeof timeReportInput>;

/** Wpis zdaniem albo głosem: tylko podgląd (nic nie zapisuje). `clientId` = klient wybrany z kandydatów. */
export const parseTimeEntryInput = z.object({
  text: z.string().trim().min(1, { message: 'Wpisz zdanie' }).max(500, { message: 'Najwyżej 500 znaków' }),
  clientId: idSchema.optional(),
  /**
   * Osoba potwierdziła, że wpis kończy się następnego dnia (MVP §7: przejście przez północ musi jawnie wskazywać dzień
   * końca). Bez tego zdanie przez północ nie daje gotowego `draft`.
   */
  confirmEndNextDay: z.boolean().optional(),
});

export const timeClientSummaryInput = z.object({ clientId: idSchema });
export const timeUnbilledSummaryInput = z.object({ scope: timeEntryScopeSchema.default('mine') }).default({});
export const timeProjectUsageInput = z.object({ projectId: idSchema });
export const timeOptionsInput = z.object({}).optional();
export const timeTaskOptionsInput = z
  .object({ clientId: idSchema.nullable().optional(), projectId: idSchema.nullable().optional() })
  .default({});

// ── Stawka partnera i rozliczenie partnera (DECISIONS 2026-10-08 [P]) ─────────

/** Domyślna stawka partnera osoby (Zespół → uprawnienia osoby). Odczyt: owner / admin / manager albo sama osoba. */
export const memberPartnerRateInput = z.object({ userId: idSchema });
/** Zmiana domyślnej stawki partnera: owner / admin. Nierozliczone wpisy bez stawki w projekcie dostają nową stawkę. */
export const setMemberPartnerRateInput = z.object({ userId: idSchema, defaultPartnerRate: rateSchema.nullable() });

const settlementPeriodShape = { userId: idSchema, from: localDateSchema, to: localDateSchema };
function settlementPeriodRefinement(v: { from: string; to: string }, ctx: z.RefinementCtx): void {
  if (v.to < v.from) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['to'], message: 'Koniec przed początkiem' });
  else if (diffDaysLocal(v.from, v.to) >= MAX_TIME_RANGE_DAYS) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['to'], message: 'Najwyżej rok naraz' });
  }
}

/** Podgląd rozliczenia: nierozliczone wpisy partnera ze stawką partnera, które zaczęły się w okresie. */
export const partnerSettlementPreviewInput = z.object(settlementPeriodShape).superRefine(settlementPeriodRefinement);
/**
 * „Zamknij rozliczenie”: dokładnie wpisy z podglądu (`entryIds`) — zmiana od podglądu albo drugie zamknięcie tych samych
 * wpisów = CONFLICT.
 */
export const closePartnerSettlementInput = z
  .object({
    ...settlementPeriodShape,
    entryIds: z.array(idSchema).min(1).max(5000),
    settledOn: localDateSchema,
    method: z.enum(PARTNER_SETTLEMENT_METHODS),
    note: z.string().trim().max(500, { message: 'Najwyżej 500 znaków' }).optional(),
  })
  .superRefine(settlementPeriodRefinement);
export const reopenPartnerSettlementInput = z.object({
  id: idSchema,
  version: z.number().int().positive(),
  reason: z.string().trim().max(500, { message: 'Najwyżej 500 znaków' }).optional(),
});
/** Lista rozliczeń: owner / admin / manager — wszystkie (opcjonalnie jednej osoby); pozostali — tylko własne. */
export const listPartnerSettlementsInput = z
  .object({ userId: idSchema.optional(), limit: z.number().int().min(1).max(200).default(50) })
  .default({});

/** Suma kwot partnera (PLN, reguła pozycji: stawka partnera × łączny czas grupy o tej samej stawce). */
export interface TimePartnerTotals {
  /** czas wpisów ze stawką partnera */
  seconds: number;
  amount: string;
  /** jeszcze w żadnym zamkniętym rozliczeniu partnera („do wypłaty”) */
  unsettledAmount: string;
  unsettledSeconds: number;
  currency: string;
}

export interface MemberPartnerRate {
  userId: string;
  defaultPartnerRate: string | null;
  /** może zmienić (owner / admin) */
  canEdit: boolean;
}

export interface SetMemberPartnerRateResult extends MemberPartnerRate {
  /** nierozliczone wpisy, którym zmieniła się stawka partnera */
  updatedEntries: number;
}

export interface PartnerSettlementEntry {
  id: string;
  date: LocalDate;
  start: LocalTime;
  end: LocalTime;
  durationSeconds: number;
  description: string;
  client: { id: string; name: string } | null;
  project: { id: string; name: string } | null;
  partnerRate: string;
  partnerAmount: string;
}

export interface PartnerSettlementPreview {
  user: TimeEntryPerson;
  range: LocalDateRange;
  entryCount: number;
  seconds: number;
  amount: string;
  currency: string;
  /** od najstarszego */
  entries: PartnerSettlementEntry[];
  /** nierozliczone wpisy tej osoby w okresie bez stawki partnera (nie wchodzą do rozliczenia) */
  withoutRate: { entryCount: number; seconds: number };
}

export interface PartnerSettlementDto {
  id: string;
  version: number;
  user: TimeEntryPerson;
  range: LocalDateRange;
  status: PartnerSettlementStatus;
  entryCount: number;
  seconds: number;
  amount: string;
  currency: string;
  settledOn: LocalDate;
  method: PartnerSettlementMethod;
  note: string | null;
  closedBy: { userId: string; name: string } | null;
  closedAt: Date;
  reopenedBy: { userId: string; name: string } | null;
  reopenedAt: Date | null;
  reopenReason: string | null;
  /** może otworzyć ponownie (owner / admin, rozliczenie zamknięte) */
  canReopen: boolean;
}

export interface PartnerSettlementList {
  items: PartnerSettlementDto[];
  /** tworzy i otwiera ponownie rozliczenia (owner / admin) */
  canManage: boolean;
  /** partnerzy do wyboru w „Nowe rozliczenie” (osoby z jakąkolwiek stawką partnera); pusta bez `canManage` */
  partners: TimeEntryPerson[];
  /**
   * Do wypłaty (Przegląd Fali 11): nierozliczone wpisy ze stawką partnera per osoba, wszystkie okresy — owner / admin /
   * manager: wszyscy partnerzy, pozostali: tylko własne. `from` / `to` = dzień najstarszego i najnowszego wpisu.
   */
  outstanding: PartnerOutstanding[];
}

export interface PartnerOutstanding {
  user: TimeEntryPerson;
  entryCount: number;
  seconds: number;
  amount: string;
  currency: string;
  from: string;
  to: string;
}

// ── Wyjścia usług (DTO). Bez „Widzi stawki i kwoty” pola pieniężne są POMINIĘTE (nie null). ──

export interface TimeEntryPerson {
  userId: string;
  name: string;
  initials: string;
  avatarColor: string | null;
}

export interface TimeEntryDto {
  id: string;
  version: number;
  userId: string;
  user: TimeEntryPerson | null;
  clientId: string | null;
  client: { id: string; name: string; color: string | null } | null;
  projectId: string | null;
  project: { id: string; name: string; isInternal: boolean } | null;
  serviceId: string | null;
  service: { id: string; name: string } | null;
  taskId: string | null;
  /** tylko z modułem Zadania */
  task: { id: string; number: number; title: string } | null;
  /** dzień i godziny lokalnie (Europe/Warsaw) */
  date: LocalDate;
  start: LocalTime;
  end: LocalTime;
  /** dzień końca, gdy inny niż `date` (wpis przez północ) */
  endDate: LocalDate | null;
  startedAt: Date;
  endedAt: Date;
  durationSeconds: number;
  description: string;
  billable: boolean;
  currency: string;
  /** snapshot stawki (null = brak stawki); tylko z „Widzi stawki i kwoty” */
  rate?: string | null;
  /** tylko z „Widzi stawki i kwoty” i dostępem do całego klienta wpisu (nie tylko przez projekt) */
  amount?: string;
  /**
   * migawka stawki partnera (PLN/h, null = brak) — tylko osoba wpisu i owner / admin / manager (DECISIONS 2026-10-08
   * [P] „Stawka partnera”), niezależnie od „Widzi stawki i kwoty”
   */
  partnerRate?: string | null;
  /** kwota partnera (PLN) — jak `partnerRate` */
  partnerAmount?: string;
  /** zamknięte rozliczenie partnera, w którym jest wpis — jak `partnerRate` */
  partnerSettlementId?: string | null;
  source: TimeEntrySource;
  billingState: TimeEntryBillingState;
  billingDraftId: string | null;
  /** zamknięty w rozliczeniu (zestawienie czeka na akceptację albo jest wystawione) — bez edycji */
  closed: boolean;
  archivedAt: Date | null;
  createdBy: string | null;
  createdAt: Date;
  updatedAt: Date;
  /** osoba może edytować (własny wpis albo Manager / Admin, moduł Czas: Edycja, wpis nie jest zamknięty) */
  canEdit: boolean;
}

export const TIME_ENTRY_WARNING_CODES = ['OVERLAP', 'NO_RATE'] as const;
export type TimeEntryWarningCode = (typeof TIME_ENTRY_WARNING_CODES)[number];

export interface TimeEntryOverlapRef {
  id: string;
  description: string;
  date: LocalDate;
  start: LocalTime;
  end: LocalTime;
}

export interface TimeEntryWarning {
  code: TimeEntryWarningCode;
  message: string;
  /** wpisy, na które nachodzi zapisany wpis (`OVERLAP`) */
  entries?: TimeEntryOverlapRef[];
}

/** Wynik zapisu: wpis + ostrzeżenia (zapis się udał, ostrzeżenie tylko informuje). */
export interface TimeEntryMutationResult {
  entry: TimeEntryDto;
  warnings: TimeEntryWarning[];
}

/** Kwoty grupy w jednej walucie (waluty się nie sumuje). */
export interface TimeTotalsCurrency {
  currency: string;
  amount: string;
  unbilledAmount: string;
}

/**
 * Sumy grupy (dzień, tydzień, klient, projekt). Kwoty tylko z „Widzi stawki i kwoty”. Kwoty w różnych walutach
 * nigdy nie są sumowane: `amount` i `unbilledAmount` są w walucie `currency` (PLN, gdy występuje), kwoty w innych
 * walutach są w `otherCurrencies`.
 */
export interface TimeTotals {
  seconds: number;
  billableSeconds: number;
  nonBillableSeconds: number;
  entryCount: number;
  /**
   * kwota w walucie `currency` wg reguły pozycji (DECISIONS 2026-10-05 [P]): Σ round(stawka × łączny czas grupy o tej
   * samej stawce, 2) — tyle wyjdzie w zestawieniu z tych wpisów; kwoty wpisów (`TimeEntryDto.amount`) są informacyjne
   */
  amount?: string;
  /** rozliczane, jeszcze w żadnym zestawieniu (stan `unbilled`, jak Rozliczenia i Dziś) */
  unbilledSeconds: number;
  /** w walucie `currency` */
  unbilledAmount?: string;
  /** waluta `amount` i `unbilledAmount` (z „Widzi stawki i kwoty”) */
  currency?: string;
  /** kwoty w innych walutach niż `currency`; brak = jedna waluta */
  otherCurrencies?: TimeTotalsCurrency[];
  /** kwoty partnera z wpisów, których kwotę partnera osoba widzi (swoje; owner / admin / manager — wszystkie) */
  partner?: TimePartnerTotals;
}

export interface TimeEntryDay {
  date: LocalDate;
  totals: TimeTotals;
  entries: TimeEntryDto[];
}

export interface TimeEntryWeek {
  /** poniedziałek */
  weekStart: LocalDate;
  weekEnd: LocalDate;
  totals: TimeTotals;
  days: TimeEntryDay[];
}

export interface TimeEntryListResult {
  range: LocalDateRange;
  totals: TimeTotals;
  /** tygodnie od najnowszego, w tygodniu dni od najnowszego (tylko dni z wpisami) */
  weeks: TimeEntryWeek[];
  /** lista ucięta do `limit` (sumy liczone z pokazanych wpisów) */
  truncated: boolean;
}

export interface TimeReportProject {
  projectId: string | null;
  name: string;
  isInternal: boolean;
  totals: TimeTotals;
  /** od najnowszego */
  entries: TimeEntryDto[];
}

export interface TimeReportClient {
  clientId: string | null;
  name: string;
  color: string | null;
  totals: TimeTotals;
  projects: TimeReportProject[];
}

export interface TimeReport {
  period: TimeReportPeriod;
  range: LocalDateRange;
  scope: TimeEntryScope;
  totals: TimeTotals & { clientCount: number; projectCount: number };
  /** klient z największą kwotą (bez kwot: czasem) niezafakturowaną (kafelek „Niezafakturowane”) */
  topUnbilled: { clientId: string; name: string; seconds: number; amount?: string; currency?: string } | null;
  /** klienci z niezafakturowanym czasem (notatka kafelka przy kilku klientach: „18:00 · 2 klientów”) */
  unbilledClientCount: number;
  /** każdy dzień zakresu (wykres), gdy zakres ma najwyżej 92 dni; inaczej pusta lista */
  days: { date: LocalDate; seconds: number; billableSeconds: number }[];
  /** klient → projekt → wpisy, od największego czasu */
  clients: TimeReportClient[];
  truncated: boolean;
}

/** Profil klienta: godziny w bieżącym i poprzednim miesiącu, niezafakturowane. */
export interface TimeClientSummary {
  clientId: string;
  currentMonth: LocalDateRange & { seconds: number };
  previousMonth: LocalDateRange & { seconds: number };
  /** `amount` w walucie `currency`, inne waluty w `otherAmounts` (tylko z „Widzi stawki i kwoty”) */
  unbilled: { seconds: number; entryCount: number } & Partial<MoneyTotal>;
  lastEntryDate: LocalDate | null;
}

/** Dziś: niezafakturowany czas i kwota (Moje / Zespół). Kwoty per waluta (`MoneyTotal`), bez sumowania walut. */
export interface TimeUnbilledSummary extends Partial<MoneyTotal> {
  scope: TimeEntryScope;
  seconds: number;
  entryCount: number;
  clientCount: number;
  /** od największej kwoty (bez kwot: czasu), najwyżej 5 */
  clients: ({ clientId: string; name: string; color: string | null; seconds: number } & Partial<MoneyTotal>)[];
  /**
   * „Moje do wypłaty” (Moje) / kwoty partnerów do wypłaty (Zespół, owner / admin / manager): nierozliczone wpisy ze
   * stawką partnera, których kwotę partnera osoba widzi (wszystkie okresy); brak = żadnego takiego wpisu
   */
  partner?: { seconds: number; amount: string; currency: string };
}

/** Projekt: wykorzystanie czasu (wpisy projektu w zakresie osoby). */
export interface TimeProjectUsage {
  projectId: string;
  budgetSeconds: number | null;
  totals: TimeTotals;
  currentMonthSeconds: number;
  people: (TimeEntryPerson & { seconds: number })[];
  /** „Koszt partnerów” (owner / admin / manager): kwoty partnera wszystkich wpisów projektu */
  partnerCost?: TimePartnerTotals;
}

export interface TimeFormOptions {
  clients: {
    id: string;
    name: string;
    color: string | null;
    partial: boolean;
    /** stawka i waluta klienta — tylko z „Widzi stawki i kwoty” */
    defaultRate?: string | null;
    currency?: string;
  }[];
  projects: {
    id: string;
    name: string;
    clientId: string | null;
    isInternal: boolean;
    billableDefault: boolean;
    status: string;
  }[];
  services: { id: string; name: string; clientId: string }[];
  members: TimeEntryPerson[];
  /** ostatnio używane połączenia klient · projekt (najnowsze pierwsze, najwyżej 6) */
  recent: { clientId: string | null; projectId: string | null }[];
  /** wpis za inną osobę (Manager, Admin) */
  canChooseUser: boolean;
}

export interface TimeTaskOption {
  id: string;
  number: number;
  title: string;
  clientId: string | null;
  projectId: string | null;
}

export interface TimeParseCandidate {
  id: string;
  name: string;
  color: string | null;
  /** tylko z „Widzi stawki i kwoty” */
  defaultRate?: string | null;
  /** waluta klienta (do stawki i kwoty podglądu) */
  currency?: string;
}

/** Podgląd wpisu ze zdania (nic nie jest zapisane; `draft` przekazuje UI do `time.create` po potwierdzeniu). */
export interface TimeEntryParsePreview {
  text: string;
  clientHint: string | null;
  client: TimeParseCandidate | null;
  project: { id: string; name: string } | null;
  /** klienci do wyboru (niejednoznaczny albo nieznany klient) */
  candidates: TimeParseCandidate[];
  date: LocalDate;
  start: LocalTime | null;
  end: LocalTime | null;
  endDate: LocalDate | null;
  durationSeconds: number | null;
  description: string;
  billable: boolean;
  /** kwota podglądu (z „Widzi stawki i kwoty”) w walucie `currency` */
  amount?: string;
  currency?: string;
  ambiguities: { code: string; message: string }[];
  /** wejście do `time.create`, gdy klient i czas są rozstrzygnięte; inaczej null */
  draft: {
    clientId: string | null;
    projectId: string | null;
    date: LocalDate;
    start: LocalTime;
    end: LocalTime;
    endDate: LocalDate | null;
    description: string;
  } | null;
}

/** Eksport CSV / PDF: raport + nagłówek (workspace, filtry opisane po polsku). */
export interface TimeExportModel {
  report: TimeReport;
  showMoney: boolean;
  /** kolumny i suma partnera (raport ma kwoty partnera, które osoba widzi) */
  showPartner: boolean;
  generatedAt: Date;
  workspace: { name: string; slug: string; website: string | null; contact: string | null; ownerName: string | null };
  filters: { client: string | null; project: string | null; user: string | null; billable: boolean | null };
}
